import { EditOutlined } from '@ant-design/icons';
import { Button, notification, Table, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useLazyGetChainsQuery } from '@app/api/Chains';
import { Error403Unauthorized } from '@app/components';
import {
  BRANCHES_LABEL,
  BRANCHES_PREVIEW_PATH,
  EDIT_CHAIN,
  MOBILE,
  PREFIX,
  RIGHTS_CHAINS,
  RIGHTS_IS_EDIT,
  USER_PRINT_ERROR,
} from '@app/constants';
import { navigation } from '@app/data';
import {
  useAccessRights,
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NChains } from '@app/types/Chains';
import { changeStandardPaginationUi } from '@app/utils';

import type { DataType } from '@pages/Chains/ChainsList/types/types';

import { adaptChainsToRedux } from '@pages/Chains/ChainsList/utils/adaptChainsToRedux';

import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FC } from 'react';

import '@pages/Chains/ChainsList/styles/Chains.desktop.scss';
import '@pages/Chains/ChainsList/styles/Chains.tablet.scss';

const { Text } = Typography;

export const ChainsList: FC = () => {
  const { hasUserRight } = useAccessRights(RIGHTS_CHAINS, RIGHTS_IS_EDIT);
  const {
    userAccessRights: {
      Chains: { IsOn: hasUserRightToModelView },
    },
  } = useNewUserAccessRights();
  const { userId, database, activeBranchId } = useUserPrint();
  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const [chains, setChains] = useState<NChains.Chain[]>([]);
  const [, setServerError] = useState('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
    size: isMobile ? 'small' : 'default',
    showSizeChanger: true,
    showTotal: (total, range) => `Показаны ${range[0]}-${range[1]} из ${total}`,
  });
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [, contextHolder] = notification.useNotification();

  const [getChains] = useLazyGetChainsQuery();

  useEffect(() => {
    const fetchChains = async () => {
      if (activeBranchId) {
        try {
          setLoading(true);
          const fetchedChains = await getChains({
            ActiveBranchId: activeBranchId,
            isActive: true,
            isDeleted: true,
          }).unwrap();
          const adaptedChains = fetchedChains.Chains
            ? fetchedChains.Chains.map((item) => adaptChainsToRedux(item))
            : [];
          setChains(adaptedChains);
        } catch (error) {
          setServerError(handleCatchError(error));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchChains();
  }, [activeBranchId, getChains]);

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [chains, isMobile]);

  if (!userId || !database || !activeBranchId) {
    return <div>{USER_PRINT_ERROR}</div>;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: generateSpanWithDataTestId('Название', 'name'),
      dataIndex: 'name',
      render: (text, record) => (
        <div style={{ opacity: record.status ? '50%' : 100 }}>
          {record.status ? (
            <Text>{generateSpanWithDataTestId(text, 'chain-name')}</Text>
          ) : (
            <Link to={`/chains/edit/?id=${record.key}`}>
              {generateSpanWithDataTestId(text, 'chain-name')}
            </Link>
          )}
          {record.status && (
            <div style={{ color: 'red' }} data-testid="archive-chain">
              В архиве
            </div>
          )}
        </div>
      ),
    },
    {
      title: generateSpanWithDataTestId(BRANCHES_LABEL, 'branches'),
      dataIndex: 'countBranches',
      render: (text, record) => (
        <Link
          to={`${BRANCHES_PREVIEW_PATH}?id=${record.key}`}
          style={{ opacity: record.status ? '50%' : 100 }}
        >
          {generateSpanWithDataTestId(text, 'chain-branches')}
        </Link>
      ),
    },
    {
      render: (record) => (
        <>
          {hasUserRight && !record.status && (
            <Tooltip title="Редактировать сеть">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{ opacity: record.status ? '50%' : 100 }}
                className="chain-edit"
                data-testid="edit-chain"
              />
            </Tooltip>
          )}
        </>
      ),
      width: '10%',
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPagination({
      ...pagination,
      showTotal: (total, range) =>
        `Показаны ${range[0]}-${range[1]} из ${total}`,
    });
  };

  const handleEdit = (record: DataType) => {
    navigate(
      `${navigation.find(({ id: _id }) => _id === EDIT_CHAIN)?.path}?id=${
        record.key
      }`
    );
  };

  const data: DataType[] = chains.map((chain: NChains.Chain, i) => {
    const item = {
      key: chain?.id || i,
      name: chain?.name || '',
      countBranches: chain?.countBranches || 0,
      status: chain?.isDeleted ? 'В архиве' : '',
    };
    return item;
  });

  if (!hasUserRightToModelView) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}chains`}>
      {contextHolder}
      <Table
        rowClassName={(row) =>
          `${row.status ? 'archived-chain-item' : 'chain-item'}`
        }
        columns={columns}
        dataSource={data}
        pagination={pagination}
        onChange={handleTableChange}
        loading={isLoading}
      />
    </div>
  );
};
